import React from "react";
import "./tnc.css";

export const Tnc = () => {
  return (
    <div className="tnc-page">
      <div className="tnc-container">
        <h1>Terms of Service for LetBabyTalk</h1>
        <h2>Operated by CradleAI (Abu Dhabi, UAE)</h2>

        <p>
          Welcome to LetBabyTalk (the “App”). These Terms of Service (“Terms“)
          govern your access to and use of the App and all related services
          provided by CradleAI (“Company”, “we”, “our”, or “us”). By creating an
          account, accessing, or using the App, you acknowledge that you have
          read, understood, and agree to be bound by these Terms as well as our
          Privacy Policy. If you do not agree with these Terms, you must
          discontinue use of the App immediately.
        </p>

        {/* 1. NON-MEDICAL DISCLAIMER */}
        <h3 className="tnc-section-title">1. Non-Medical Nature of the App</h3>

        <p>
          1.1 LetBabyTalk is not a medical device. The App’s AI-based crying
          analysis provides informational insights only. The App does not
          provide medical advice, diagnosis, or treatment. Any predictions,
          explanations, or insights generated by the App regarding infant crying
          or behavior are probabilistic and may be incomplete, inaccurate, or
          inconsistent. You acknowledge that such insights must not be relied
          upon for medical or emergency decision-making.
        </p>

        <p>
          1.2 Parental Responsibility: You acknowledge that you remain fully
          responsible for the care, safety, well-being, and medical needs of
          your child. CradleAI does not assume responsibility for any outcomes
          relating to actions you take based on App-generated insights.
        </p>

        <p>
          1.3 Medical Concerns: If you have any concerns regarding your child’s
          health, you must immediately seek advice from a qualified healthcare
          professional. The App is not a substitute for professional guidance.
        </p>

        {/* 2. Eligibility */}
        <h3 className="tnc-section-title">2. Eligibility</h3>

        <p>
          2.1 You must be at least 18 years old or the age of legal majority in
          your jurisdiction to use the App.
        </p>

        <p>
          2.2 The App is intended for parents and guardians. It is not directed
          at children.
        </p>

        {/* 3. ACCOUNTS AND SECURITY */}
        <h3 className="tnc-section-title">3. Accounts and Security</h3>

        <p>
          3.1 Account Creation: You may use the App without registering or may
          choose to create an account using email or phone number. You agree to
          provide accurate, complete, and up-to-date information.
        </p>

        <p>
          3.2 Account Security: You are responsible for maintaining the
          confidentiality of your login credentials and for all actions taken
          under your account.
        </p>

        <p>
          3.3 Unauthorized Access: If you suspect unauthorized access to your
          account, you must notify us immediately. CradleAI is not liable for
          losses resulting from your failure to protect your credentials.
        </p>

        {/* 4. SERVICES AND FUNCTIONALITY */}
        <h3 className="tnc-section-title">4. Services and Functionality</h3>

        <p>
          4.1 Core Functionality: The App analyzes infant crying audio to offer
          potential explanations for the baby’s behavior. These outputs are
          generated by AI models based on probabilistic analysis and should not
          be interpreted as factual, guaranteed, or medically relevant.
        </p>

        <p>
          4.2 Free Service: The App is currently provided free of charge.
          CradleAI reserves the right to introduce optional paid features in the
          future.
        </p>

        <p>
          4.3 Service Modifications: Features may be updated, improved, changed,
          or removed at any time without prior notice.
        </p>

        {/* 5. SUBSCRIPTIONS & REFUNDS */}
        <h3 className="tnc-section-title">
          5. Subscription, Billing, and Refund Policy
        </h3>

        <p>
          5.1 Subscription Features: Future versions of the App may introduce
          optional paid or subscription-based services. These will be clearly
          disclosed before purchase.
        </p>

        <p>
          5.2 Automatic Renewal: If a subscription is offered, it may renew
          automatically at the end of each billing cycle unless you cancel
          before renewal.
        </p>

        <p>
          5.3 Cancellation: You may cancel a subscription at any time using the
          App Store or Google Play settings. Cancellation becomes effective at
          the end of the billing period.
        </p>

        <p>
          5.4 Refunds: Refunds are governed by the policies of the platform on
          which you made the purchase (Apple App Store, Google Play, etc.).
          CradleAI does not issue direct refunds unless required by applicable
          law.
        </p>

        <p>
          5.5 Price Changes: We may adjust subscription prices with advance
          notice. Your continued use after the price change constitutes
          acceptance.
        </p>

        {/* 6. PERSONAL DATA */}
        <h3 className="tnc-section-title">6. Personal Data and Privacy</h3>

        <p>
          6.1 Data We Collect: Depending on your usage, we may collect:
          <br />
          • Infant crying audio recordings <br />
          • Infant profile (e.g., birth date, gender) <br />
          • Optional parent/guardian details (name, email, phone) <br />
          • Location data (GPS, IP address) <br />
          • Device identifiers (IDFA, Android ID, GAID) <br />
          • Usage analytics and crash data <br />
          • User-generated content (notes, profile inputs, feedback)
        </p>

        <p>
          6.2 Third-Party SDKs: We may integrate secure third-party services,
          such as Amazon Web Services (AWS), Firebase Analytics, crash
          reporting tools, authentication providers, and cloud delivery
          networks.
        </p>

        <p>
          6.3 Sensitive Data: Infant audio recordings constitute sensitive
          information and are protected with elevated safeguards.
        </p>

        {/* 7. GLOBAL DATA STORAGE & TRANSFERS */}
        <h3 className="tnc-section-title">
          7. Data Storage and International Transfers
        </h3>

        <p>
          7.1 Data Location: All data is stored in Amazon Web Services (AWS)
          data centers located in Singapore.
        </p>

        <p>
          7.2 International Transfers: Because CradleAI operates globally, your
          data may be transferred or accessed from:
          <br />
          • UAE <br />
          • Singapore <br />
          • Other jurisdictions where trusted partners operate
        </p>

        <p>
          7.3 GDPR Compliance: EU/EEA user data may be transferred outside the
          EEA under GDPR-compliant mechanisms such as Standard Contractual
          Clauses (SCCs).
        </p>

        <p>
          7.4 China PIPL Compliance: Users in Mainland China consent to data
          being transferred outside China for processing.
        </p>

        <p>
          7.5 Singapore PDPA Compliance: All data hosted in Singapore complies
          with PDPA requirements.
        </p>

        <p>
          7.6 U.S. CCPA & COPPA Compliance: We do not “sell” personal
          information. Infant-related data is safeguarded with enhanced child
          data protections.
        </p>

        {/* 8. INTELLECTUAL PROPERTY */}
        <h3 className="tnc-section-title">8. Intellectual Property</h3>

        <p>
          All software, algorithms, AI models, features, designs, trademarks,
          logos, images, and content displayed within the App are the exclusive
          property of CradleAI or its licensors. You may not reproduce,
          distribute, reverse-engineer, transmit, create derivative works, or
          otherwise exploit any part of the App without prior written consent.
        </p>

        {/* 9. ACCEPTABLE USE */}
        <h3 className="tnc-section-title">9. Acceptable Use</h3>

        <p>You agree not to engage in any of the following prohibited acts:</p>

        <p>
          • Interfere with the normal operation of the App <br />
          • Attempt to extract data sets, source code, or AI models <br />
          • Upload or transmit harmful or illegal content <br />
          • Use the App for competitive research or scraping <br />
          • Rely on the App to make medical decisions <br />
          • Attempt unauthorized access to systems, accounts, or data
        </p>

        {/* 10. DISCLAIMERS */}
        <h3 className="tnc-section-title">10. Disclaimers</h3>

        <p>
          10.1 No Medical Advice: LetBabyTalk is not intended to diagnose,
          treat, cure, or prevent any disease or medical condition.
        </p>

        <p>
          10.2 Accuracy Disclaimer: CradleAI does not guarantee the accuracy or
          completeness of predictions or outputs.
        </p>

        <p>
          10.3 Parenting Responsibility: CradleAI is not responsible for any
          decisions you make based on App information.
        </p>

        {/* 11. LIMITATION OF LIABILITY */}
        <h3 className="tnc-section-title">11. Limitation of Liability</h3>

        <p>
          To the maximum extent permitted by law, CradleAI shall not be liable
          for any indirect, incidental, special, punitive, or consequential
          damages. As the App is provided free of charge, CradleAI’s total
          liability for any claim shall not exceed USD $0.
        </p>

        {/* 12. SERVICE TERMINATION */}
        <h3 className="tnc-section-title">
          12. Service Availability and Termination
        </h3>

        <p>
          We may modify, suspend, or discontinue the App or any feature at any
          time. You may discontinue use at any time. User data may be deleted
          after prolonged inactivity or service termination.
        </p>

        {/* 13. GOVERNING LAW */}
        <h3 className="tnc-section-title">
          13. Governing Law and Dispute Resolution
        </h3>

        <p>
          These Terms are governed by the laws of Abu Dhabi, United Arab
          Emirates. Disputes shall be resolved exclusively by the courts or
          arbitration authorities in Abu Dhabi unless required otherwise by
          mandatory local law.
        </p>

        {/* 14. UPDATES */}
        <h3 className="tnc-section-title">14. Updates to These Terms</h3>

        <p>
          CradleAI may update these Terms from time to time. Continued use of
          the App constitutes acceptance of the updated Terms.
        </p>

        {/* 15. CONTACT */}
        <h3 className="tnc-section-title">15. Contact Information</h3>

        <p>
          {/* CradleAI
          <br />
          Abu Dhabi, United Arab Emirates
          <br /> */}
          Email: letbabytalk@gmail.com
        </p>
      </div>
    </div>
  );
};
